import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as sdk from '@whispers/sdk';

// ============================================================================
// Token Management
// ============================================================================

let accessToken: string | undefined;

export const getAccessToken = (): string | undefined => accessToken;

export const setAccessToken = (token: string | undefined): void => {
  accessToken = token;
};

export const clearTokens = (): void => {
  accessToken = undefined;
};

// ============================================================================
// Session invalidation callback
// ============================================================================

// Registered by AuthContext so the withRefresh wrapper can signal when the
// session has expired mid-flight (both tokens dead) and the user must be
// re-authenticated. Kept module-level to avoid circular dependencies.
let onSessionExpired: (() => void) | null = null;

export const registerSessionExpiredHandler = (handler: () => void): (() => void) => {
  onSessionExpired = handler;
  return () => {
    onSessionExpired = null;
  };
};

// ============================================================================
// API Instance
// ============================================================================

const baseUrl =
  import.meta.env.DEV && import.meta.env['VITE_API_PROXY_TARGET']
    ? '/api'
    : import.meta.env.VITE_API_BASE_URL;

const clientOptions: sdk.ClientOptions = {
  baseUrl,
  credentials: 'include',
  headers: ({ path }) => {
    const headers = new Headers();
    if (!['/auth/otp', '/auth/otp/verify', '/auth/refresh'].includes(path)) {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }
    return headers;
  },
};

// A bare client used only for the token refresh call — no retry wrapper so
// a failed refresh doesn't trigger another refresh attempt.
const bareClient = sdk.createClient(clientOptions);

// Deduplicate concurrent 401s: if a refresh is already in-flight, all
// callers await the same promise rather than firing multiple refresh requests.
let refreshInFlight: Promise<void> | null = null;

const refreshAccessToken = async (): Promise<void> => {
  const data = await bareClient.post<sdk.TokenRefreshResponse>({ path: '/auth/refresh' });
  setAccessToken(data.accessToken);
};

const ensureRefreshed = (): Promise<void> => {
  if (!refreshInFlight) {
    refreshInFlight = refreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

// Wraps every method on an Api namespace object so that a 401 HttpError
// triggers a token refresh and a single retry, keeping the SDK client itself
// close to the wire.
const withRefresh = <T extends Record<string, (...args: never[]) => Promise<unknown>>>(
  namespace: T,
): T => {
  // eslint-disable-next-line no-unsafe-type-assertion
  return Object.fromEntries(
    Object.entries(namespace).map(([key, fn]) => [
      key,
      async (...args: never[]) => {
        try {
          return await fn(...args);
        } catch (err) {
          if (err instanceof sdk.HttpError && err.statusCode === 401) {
            try {
              await ensureRefreshed();
            } catch {
              // Both tokens are dead — clear storage and signal the app so
              // it can update auth state and redirect to sign-in.
              clearTokens();
              onSessionExpired?.();
              throw err;
            }
            return await fn(...args);
          }
          throw err;
        }
      },
    ]),
  ) as T;
};

// Build the api, then wrap every namespace with the refresh-retry logic.
const _api = sdk.createApi(clientOptions);

export const api: sdk.Api = {
  auth: withRefresh(_api.auth),
  cards: withRefresh(_api.cards),
  customers: withRefresh(_api.customers),
  decks: withRefresh(_api.decks),
  draws: withRefresh(_api.draws),
  feeds: withRefresh(_api.feeds),
  notes: withRefresh(_api.notes),
  products: withRefresh(_api.products),
  readings: withRefresh(_api.readings),
  spreads: withRefresh(_api.spreads),
  subscriptions: withRefresh(_api.subscriptions),
  users: withRefresh(_api.users),
  push: withRefresh(_api.push),
};

// ============================================================================
// Shared Types
// ============================================================================

export type QueryOptions = {
  enabled?: boolean;
};

// MeUser is the User object returned within GetMeResponse
export type MeUser = sdk.User;

// ============================================================================
// Auth Hooks
// ============================================================================

export const useMe = (options?: QueryOptions) =>
  useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.users.getMe(),
    retry: false,
    ...(options?.enabled !== undefined && { enabled: options.enabled }),
  });

export const useUser = (userId: string) =>
  useQuery({
    queryKey: ['auth', 'user', userId],
    queryFn: () => api.users.get({ userId }),
    enabled: !!userId,
  });

export const useUnlockedCards = (options?: QueryOptions) =>
  useQuery({
    queryKey: ['auth', 'unlockedCards'],
    queryFn: () => api.users.getUnlockedCards(),
    ...(options?.enabled !== undefined && { enabled: options.enabled }),
  });

export const useRequestOtp = () =>
  useMutation({
    mutationFn: (body: sdk.OtpRequest) => api.auth.requestOtp(body),
  });

export const useVerifyOtp = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: sdk.OtpVerifyResponse) => void;
  onError?: (error: unknown) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: sdk.OtpVerifyRequest) => api.auth.verifyOtp(body),
    onSuccess: (data) => {
      setAccessToken(data.accessToken);
      void queryClient.invalidateQueries({ queryKey: ['auth'] });
      onSuccess?.(data);
    },
    onError: (error) => {
      clearTokens();
      void queryClient.invalidateQueries({ queryKey: ['auth'] });
      onError?.(error);
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.auth.logout(),
    onSuccess: () => {
      clearTokens();
      // Remove all cached data so no stale data from the previous session
      // leaks through if another user signs in on the same browser.
      queryClient.removeQueries();
    },
  });
};

// ============================================================================
// Cards Hooks
// ============================================================================

export const useCards = (params?: sdk.GetCardsQuery, options?: QueryOptions) => {
  return useQuery({
    queryKey: ['cards', params],
    queryFn: () => api.cards.list(params),
    ...(options?.enabled !== undefined && { enabled: options.enabled }),
  });
};

// ============================================================================
// Decks Hooks
// ============================================================================

export const useDecks = (options?: QueryOptions) =>
  useQuery({
    queryKey: ['decks'],
    queryFn: () => api.decks.list(),
    ...(options?.enabled !== undefined && { enabled: options.enabled }),
  });

// ============================================================================
// Spreads Hooks
// ============================================================================

export type UseSpreadsOptions = QueryOptions & sdk.GetSpreadsQuery;

export const useSpreads = (options?: UseSpreadsOptions) =>
  useQuery({
    queryKey: ['spreads', options?.include],
    queryFn: () => api.spreads.list(options?.include ? { include: options.include } : undefined),
    ...(options?.enabled !== undefined && { enabled: options.enabled }),
  });

// ============================================================================
// Readings Hooks
// ============================================================================

export const useInfiniteUserReadings = (userId: string) =>
  useInfiniteQuery({
    queryKey: ['readings', 'user', userId],
    queryFn: ({ pageParam }) =>
      api.readings.list({
        userId,
        pageSize: 5,
        ...(pageParam && { pageToken: pageParam }),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken,
  });

export const useInfiniteReadings = (
  options?: QueryOptions & Omit<sdk.GetReadingsQuery, 'pageToken'>,
) =>
  useInfiniteQuery({
    queryKey: ['readings', options?.include],
    queryFn: ({ pageParam }) =>
      api.readings.list({
        ...(options?.include && { include: options.include }),
        ...(pageParam && { pageToken: pageParam }),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken,
    ...(options?.enabled !== undefined && { enabled: options.enabled }),
  });

export const useInfiniteFeeds = (options?: QueryOptions) =>
  useInfiniteQuery({
    queryKey: ['feeds'],
    queryFn: ({ pageParam }) => api.feeds.list(pageParam ? { pageToken: pageParam } : undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken,
    ...(options?.enabled !== undefined && { enabled: options.enabled }),
  });

// ============================================================================
// Draws Hooks
// ============================================================================

export const useDrawsPatterns = (options?: QueryOptions) =>
  useQuery({
    queryKey: ['draws', 'patterns'],
    queryFn: () => api.draws.patterns(),
    ...(options?.enabled !== undefined && { enabled: options.enabled }),
  });

// ============================================================================
// Notes Hooks
// ============================================================================

export const useInfiniteNotes = (options?: QueryOptions) =>
  useInfiniteQuery({
    queryKey: ['notes'],
    queryFn: ({ pageParam }) => api.notes.list(pageParam ? { pageToken: pageParam } : undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken,
    ...(options?.enabled !== undefined && { enabled: options.enabled }),
  });

export type UseReadingOptions = QueryOptions & sdk.GetReadingPath & sdk.GetReadingQuery;

export const useReading = (options: UseReadingOptions) =>
  useQuery({
    queryKey: ['readings', options.readingId, options.include],
    queryFn: () =>
      api.readings.get({
        readingId: options.readingId,
        include: options.include ? options.include : undefined,
      }),
    enabled: (options.enabled ?? true) && !!options.readingId,
  });

export const useCreateReading = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: sdk.CreateReadingBody) => api.readings.create(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['readings'] });
      void queryClient.invalidateQueries({ queryKey: ['feeds'] });
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      void queryClient.invalidateQueries({ queryKey: ['auth', 'unlockedCards'] });
      void queryClient.invalidateQueries({ queryKey: ['feeds'] });
    },
  });
};

// ============================================================================
// Notes Hooks
// ============================================================================

type UseReadingNoteOptions = QueryOptions & sdk.GetReadingNotePath;

export const useNote = (options: UseReadingNoteOptions) =>
  useQuery({
    queryKey: ['notes', options.readingId],
    queryFn: () => api.readings.getNote(options),
    enabled: (options.enabled ?? true) && !!options.readingId,
    retry: (_, error) => 'statusCode' in error && error.statusCode !== 404,
  });

export const useSaveNote = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: sdk.UpsertNoteRequest) => api.notes.upsert(body),
    onSuccess: ({ note }) => {
      void queryClient.invalidateQueries({ queryKey: ['notes', note.readingId] });
      void queryClient.invalidateQueries({ queryKey: ['notes'] });
      void queryClient.invalidateQueries({ queryKey: ['readings'] });
      void queryClient.invalidateQueries({ queryKey: ['feeds'] });
    },
  });
};

// ============================================================================
// Billing Hooks
// ============================================================================

export type UseProductsOptions = QueryOptions & sdk.GetProductsQuery;

export const useProducts = (options?: UseProductsOptions) =>
  useQuery({
    queryKey: ['billing', 'products', options?.include],
    queryFn: () => api.products.get(options?.include ? { include: options.include } : undefined),
    ...(options?.enabled !== undefined && { enabled: options.enabled }),
  });

export const useSubscription = (options?: QueryOptions) =>
  useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: () => api.subscriptions.getCurrent(),
    ...(options?.enabled !== undefined && { enabled: options.enabled }),
  });

export const useCreateCheckout = () =>
  useMutation({
    mutationFn: api.customers.createCheckout,
  });

export const useCreatePortal = () =>
  useMutation({
    mutationFn: () => api.customers.createPortal(),
  });

// ============================================================================
// Push Hooks
// ============================================================================

export const usePushSubscriptions = (options?: QueryOptions) =>
  useQuery({
    queryKey: ['push', 'subscriptions'],
    queryFn: () => api.push.list(),
    ...(options?.enabled !== undefined && { enabled: options.enabled }),
  });

export const useSendNotification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.push.sendNotification,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['push'] });
    },
  });
};

// ============================================================================
// Admin Hooks
// ============================================================================

export const useInfiniteUsers = (query: Omit<sdk.GetUsersQuery, 'pageToken'>) =>
  useInfiniteQuery({
    queryKey: ['admin', 'users', query],
    queryFn: ({ pageParam }) =>
      api.users.list({ ...query, ...(pageParam && { pageToken: pageParam }) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken,
  });

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...body }: sdk.GetUserPath & sdk.UpdateUserRequest) =>
      api.users.update({ userId }, body),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
        queryClient.invalidateQueries({ queryKey: ['auth'] }),
      ]);
    },
  });
};
